/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 23/05/2025 19:19
 *
 */

import {
  AuthorizationService,
  DataModel,
  ModelProperty,
  NoteBuilder,
  NotificationService,
  SelectableEntity,
  SortableList,
  SortStatus,
  Validation,
  ValidationMode,
  ValidationType,
} from '@infodata-group/inf-commons';
import { MapManager } from '@infodata-group/inf-commons-tools';
import {
  Address,
  AoSharedDataService,
  AoSharedTools,
  CustomLogger,
} from '@monorepo-projects/alwayson-shared';
import { SelectableEntityBuilder } from '@monorepo-projects/inf-commons-lib';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { RgbColorInterface } from '../../interface/graph/rgb-color.interface';

export class Tools extends AoSharedTools {
  public static getSearchDataModel(dataModel): DataModel {
    const searchProperties = dataModel.properties.filter((x) => x.showOnSearch);
    const newDataModel = new DataModel();
    for (const initialProperties of searchProperties) {
      // Copy to avoid reference update
      const prop = new ModelProperty(
        initialProperties.id,
        initialProperties.title,
        initialProperties.type,
      );
      // tslint:disable-next-line:forin
      for (const key in initialProperties) {
        if (key === 'formGroup' || key === 'width' || key === 'column') {
          continue;
        }
        prop[key] = initialProperties[key];
      }
      prop.showOnForm = true;
      if (prop.searchType) {
        prop.editType = prop.searchType;
      }
      newDataModel.addProperty(prop);
    }
    return newDataModel;
  }

  public static getValidationPrimaryKey(
    translateService: TranslateService,
  ): Validation {
    return new Validation({
      type: ValidationType.regexp,
      mode: ValidationMode.error,
      rule: '^[^@&#/* ]+$',
      message: translateService.instant(
        'i18n.tools.error-validation-primary-key',
      ),
    });
  }

  public static getValidationMail(
    translateService: TranslateService,
  ): Validation {
    return new Validation({
      type: ValidationType.regexp,
      mode: ValidationMode.error,
      rule: '(^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$)',
      message: translateService.instant('i18n.tools.error-validation-mail'),
    });
  }

  public static getBbxIconFormatter(field: string): any {
    return {
      type: 'icon',
      field: field,
      values: Tools.getBbxIconFormatterValues(),
    };
  }

  public static getBbxIconFormatterValues() {
    const icons = [
      'ambulance',
      'bus',
      'car',
      'clock',
      'container-storage',
      'moped',
      'motorcycle',
      'taxi',
      'tractor',
      'trailer',
      'truck',
      'truck-container',
      'truck-monster',
      'truck-moving',
      'truck-pickup',
      'truck-plow',
      'user-hard-hat',
      'van-shuttle',
      'walking',
      'wheelchair',
    ];
    const colors = ['ed5565', '48cfad', 'f6bb42', '2f373a'];
    const formatterValues = {};
    icons.forEach((icon) => {
      formatterValues[icon] = `${icon} icon-bold`;
      colors.forEach((color) => {
        formatterValues[`${icon} ${color}`] =
          `${icon} icon-bold color-${color}`;
      });
    });
    return formatterValues;
  }

  public static copyToClipboard(data: any): void {
    try {
      if (typeof data !== 'string') {
        data = JSON.stringify(data);
      }
      navigator.clipboard.writeText(data).then(
        () => {
          CustomLogger.v('Async: Copying to clipboard was successful!');
        },
        (err) => {
          CustomLogger.e({
            text: 'Async: Could not copy text: ',
            error: err,
          });
        },
      );
    } catch (e) {
      CustomLogger.e(e);
    }
  }

  public static downloadJSON(data: string): void {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(data);
    const link = document.createElement('a');
    link.setAttribute('href', dataStr);
    link.setAttribute('download', 'menu.json');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  public static cleanUrl(url: string): string {
    const objURL = new URL(
      url.includes('http') ? url : `${document.baseURI}${url}`,
    );
    return url.replace(objURL.search, '');
  }

  public static getColor(
    index?: number,
  ): RgbColorInterface | RgbColorInterface[] {
    const colors: RgbColorInterface[] = [
      {
        r: 218,
        g: 68,
        b: 83,
      },
      {
        r: 79,
        g: 193,
        b: 233,
      },
      {
        r: 222,
        g: 220,
        b: 0,
      },
      {
        r: 172,
        g: 146,
        b: 236,
      },
      {
        r: 160,
        g: 128,
        b: 83,
      },
    ];
    return index !== undefined ? colors[index % colors.length] : colors;
  }

  public static getDefaultMapManager(
    dataService: AoSharedDataService,
    zoomAfterAutoCenter?: boolean,
  ): MapManager {
    const mapManager = new MapManager();
    mapManager.options.styles = dataService.getCurrentGmapsTheme();
    if (zoomAfterAutoCenter) {
      mapManager.options.zoomAfterAutoCenter = 12;
    }
    mapManager.zoom = 12;
    return mapManager;
  }

  public static applyDefaultSort(model: DataModel, propertyId: string): void {
    try {
      model.defaultSort = {
        sort: SortStatus.up,
        property: model.find(propertyId),
      };
    } catch (e) {
      CustomLogger.e({
        message: `Impossible to find property "${propertyId}" in model "${model.id}"`,
        error: e,
      });
    }
  }

  public static getCurrentCompany(
    authorizationService: AuthorizationService,
  ): string {
    return authorizationService.userContext.ent || null;
  }

  public static getStatusBbx(): SelectableEntity[] {
    return [
      new SelectableEntityBuilder(
        '1',
        'i18n.common.filter-bbx.driving',
        'label',
      )
        .setIcon('play fas')
        .setIconColor('#48CFAD')
        .build(),
      new SelectableEntityBuilder('0', 'i18n.common.filter-bbx.stop', 'label')
        .setIcon('stop fas')
        .setIconColor('#ED5565')
        .build(),
      new SelectableEntityBuilder(
        '9999',
        'i18n.common.filter-bbx.active',
        'label',
      )
        .setIcon('calendar-day fas')
        .setIconColor('#4A555C')
        .build(),
      new SelectableEntityBuilder(
        '2',
        'i18n.common.filter-bbx.uncertain',
        'label',
      )
        .setIcon('circle-question fas')
        .setIconColor('#FAAA0A')
        .build(),
    ];
  }

  public static getDefaultCenter(): google.maps.LatLng {
    return new google.maps.LatLng(49.8157635, 6.1315139999999815);
  }

  public static getMapInfoWindowOptions(): google.maps.InfoWindowOptions {
    return {
      headerDisabled: true,
      pixelOffset: new google.maps.Size(0, -30),
      maxWidth: 500,
      zIndex: 1000,
    };
  }

  public static geocodeAddress(
    address: Address,
    notificationService: NotificationService,
  ): Observable<google.maps.LatLng> {
    if (!address.isValid()) {
      return new BehaviorSubject(null).asObservable();
    }
    const emitter$ = new Subject<google.maps.LatLng>();
    const geocoder = new google.maps.Geocoder();
    geocoder
      .geocode({ address: address.toString() })
      .then((results) => {
        emitter$.next(results.results[0].geometry.location);
      })
      .catch((error) => {
        notificationService.addNotification(
          new NoteBuilder(
            'i18n.component.select-latlng.notification.invalid-address-title',
            'circle-exclamation',
            'i18n.component.select-latlng.notification.invalid-address-message',
          )
            .setInfoNote()
            .build(),
        );
        CustomLogger.w(error);
      });
    return emitter$;
  }

  public static geocodeLatLng(
    latLng: google.maps.LatLng,
    notificationService: NotificationService,
  ): Observable<Address> {
    const emitter$ = new Subject<Address>();
    const geocoder = new google.maps.Geocoder();
    geocoder
      .geocode({ location: latLng })
      .then((response) => {
        const components = response.results.at(0).address_components;
        const address = new Address();
        address.number =
          components.find((c) => c.types.includes('street_number'))
            ?.long_name ?? '';
        address.path =
          components.find((c) => c.types.includes('route'))?.long_name ?? '';
        address.city =
          components.find((c) => c.types.includes('locality'))?.long_name ?? '';
        address.zipCode =
          components.find((c) => c.types.includes('postal_code'))?.long_name ??
          '';
        address.country =
          components.find((c) => c.types.includes('country'))?.short_name ?? '';
        emitter$.next(address);
      })
      .catch((error) => {
        notificationService.addNotification(
          new NoteBuilder(
            'i18n.component.select-latlng.notification.invalid-address-title',
            'circle-exclamation',
            'i18n.component.select-latlng.notification.invalid-address-message',
          )
            .setInfoNote()
            .build(),
        );
        CustomLogger.w(error);
      });
    return emitter$;
  }

  public static createSortableList(
    type: 'active' | 'inactive',
  ): SortableList<SelectableEntity> {
    return new SortableList<SelectableEntity>(type, `i18n.common.${type}`, []);
  }
}
